import React from 'react';
import {
  ReasonSelection,
  NextStepView,
  CancellationConfirmation,
  PauseOffer,
  NotUsingPauseOffer,
  TechnicalIssuesPauseOffer,
  DowngradeOffer as CostDowngradeOffer,
  GoalAchievedDowngradeOffer,
  BillingPauseConfirmation,
  TechnicalIssuesPauseConfirmation,
  PlanDowngradeConfirmation,
  SupportRequestSubmittedConfirmation,
  StayConfirmation,
  ErrorConfirmation
} from '../..';
import { useCancelModalContext } from '../../../context';
import {
  CANCEL_MODAL_STEPS,
  PAUSE_OFFER_COMPONENTS,
  DOWNGRADE_OFFER_COMPONENTS,
  PAUSE_CONFIRMATION_COMPONENTS
} from '../../../constants';

/**
 * Step router component that renders the appropriate step based on current step
 * Acts as a centralized place to manage step rendering logic
 * Now uses context to access state and actions instead of props
 */
const StepRouter = () => {
  // Get all needed state and actions from context
  const { currentStep, formState } = useCancelModalContext();
  // Component registry for dynamic rendering
  const componentRegistry = {
    PauseOffer,
    NotUsingPauseOffer,
    TechnicalIssuesPauseOffer,
    CostDowngradeOffer,
    GoalAchievedDowngradeOffer,
    BillingPauseConfirmation,
    TechnicalIssuesPauseConfirmation
  };

  // Render appropriate pause offer component based on selected reason
  const renderPauseOffer = () => {
    const componentName = PAUSE_OFFER_COMPONENTS[formState.selectedReason];
    const Component = componentRegistry[componentName];

    if (!Component) {
      // Fallback to generic pause offer
      return <PauseOffer />;
    }

    return <Component />;
  };

  // Render appropriate downgrade offer component based on selected reason
  const renderDowngradeOffer = () => {
    const componentName = DOWNGRADE_OFFER_COMPONENTS[formState.selectedReason];
    const Component = componentRegistry[componentName];

    if (!Component) {
      // Fallback to cost-based downgrade
      return <CostDowngradeOffer />;
    }

    return <Component />;
  };

  // Render appropriate pause confirmation component based on selected reason
  const renderPauseConfirmation = () => {
    const componentName =
      PAUSE_CONFIRMATION_COMPONENTS[formState.selectedReason] || PAUSE_CONFIRMATION_COMPONENTS.default;
    const Component = componentRegistry[componentName];

    return <Component />;
  };

  const stepComponents = {
    [CANCEL_MODAL_STEPS.REASON_SELECTION]: <ReasonSelection />,
    [CANCEL_MODAL_STEPS.PAUSE_OFFER]: renderPauseOffer(),
    [CANCEL_MODAL_STEPS.DOWNGRADE_OFFER]: renderDowngradeOffer(),
    [CANCEL_MODAL_STEPS.PAUSE_CONFIRMATION]: renderPauseConfirmation(),
    [CANCEL_MODAL_STEPS.DOWNGRADE_CONFIRMATION]: <PlanDowngradeConfirmation />,
    [CANCEL_MODAL_STEPS.NEXT_ACTION]: <NextStepView />,
    [CANCEL_MODAL_STEPS.CANCELLATION_SUCCESS]: <CancellationConfirmation />,
    [CANCEL_MODAL_STEPS.STAY_CONFIRMATION]: <StayConfirmation />,
    [CANCEL_MODAL_STEPS.SUPPORT_REQUEST_SUBMITTED]: <SupportRequestSubmittedConfirmation />,
    [CANCEL_MODAL_STEPS.ERROR_CONFIRMATION]: <ErrorConfirmation />
  };

  return stepComponents[currentStep] || null;
};

export default StepRouter;
